import React from 'react';
import { motion } from 'framer-motion';

interface ContentPageProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  accent?: 'purple' | 'yellow' | 'orange' | 'teal';
}

const ContentPage: React.FC<ContentPageProps> = ({ title, subtitle, children, accent = 'purple' }) => {
  // Define accent color classes
  const accentColors = {
    purple: {
      light: 'from-purple-100 to-purple-50',
      medium: 'from-purple-600/20 to-purple-400/10',
      dark: 'from-purple-800 via-purple-700 to-purple-600',
      border: 'border-purple-300',
      text: 'text-purple-800'
    },
    yellow: {
      light: 'from-yellow-100 to-yellow-50',
      medium: 'from-yellow-500/20 to-yellow-300/10',
      dark: 'from-yellow-600 via-yellow-500 to-yellow-400',
      border: 'border-yellow-300',
      text: 'text-yellow-700'
    },
    orange: {
      light: 'from-orange-100 to-orange-50',
      medium: 'from-orange-500/20 to-orange-300/10',
      dark: 'from-orange-600 via-orange-500 to-orange-400',
      border: 'border-orange-300',
      text: 'text-orange-700'
    },
    teal: {
      light: 'from-teal-100 to-teal-50',
      medium: 'from-teal-500/20 to-teal-300/10',
      dark: 'from-teal-600 via-teal-500 to-teal-400',
      border: 'border-teal-300',
      text: 'text-teal-700'
    }
  };

  const colors = accentColors[accent];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full relative"
    >
      {/* Decorative shapes */}
      <div className={`absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-bl ${colors.medium} rounded-full blur-xl opacity-50 z-0`}></div>
      <div className={`absolute -bottom-10 -left-10 w-32 h-32 bg-gradient-to-tr ${colors.medium} rounded-full blur-xl opacity-50 z-0`}></div>
      
      {/* Title with decorative elements */}
      <div className="relative z-10 mb-8">
        <div className="flex items-center mb-2">
          <div className={`w-6 h-1 bg-gradient-to-r ${colors.dark} mr-3`}></div>
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-800 via-teal-500 to-yellow-500">
            {title}
          </h2>
        </div>
        {subtitle && (
          <p className={`text-lg ${colors.text} mt-2 mb-3 max-w-2xl`}>{subtitle}</p>
        )}
        <div className={`h-0.5 w-24 bg-gradient-to-r ${colors.dark} mt-2`}></div>
      </div>

      {/* Content area with subtle background */}
      <div className="relative z-10 text-gray-700 p-6 ">
        {children}
      </div>
    </motion.div>
  );
};

export default ContentPage;
